import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { ConfigProvider, App as AntApp } from "antd";
import { useAuth } from "@/hooks/useAuth";
import LoginPage from "@/pages/LoginPage";
import ProjectsPage from "@/pages/ProjectsPage";
import ProjectsDemoPage from "@/pages/ProjectsDemoPage";
import PhotosPage from "@/pages/PhotosPage";
import ReportPage from "@/pages/ReportPage";
import MobileAppPage from "@/pages/MobileAppPage";
import VideosPage from "@/pages/VideosPage";
import CloudSyncPage from "@/pages/CloudSyncPage";
import MapPage from "@/pages/LeafletMapPage";
import TestSuitePage from "@/pages/TestSuitePage";
import AccountPage from "@/pages/AccountPage";
import BetaPage from "@/pages/BetaPage";
import UsagePage from "@/pages/UsagePage";
import SharedProjectPage from "@/pages/SharedProjectPage";
import SharedGalleryPage from "@/pages/SharedGalleryPage";
import {
  AdminPage,
  MobileParamsPage,
  PlansPage,
  CloudLimitsPage,
  UsersStatsPage,
  UsbCatalogPage,
  SubscriptionsPage,
  BetaCodesPage,
  ServiceAccessPage,
} from "@/pages/admin";
import AdminGuard from "@/components/AdminGuard";
import { useEffect } from "react";
import { signInWithEmailAndPassword } from "firebase/auth";
import { auth } from "@/config/firebase";

// Create a client for React Query
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

// Session storage key to track if auto-login was attempted
const DEV_AUTO_LOGIN_KEY = "dev_auto_login_done";

// Auto-login in development mode (only on initial load, not after logout)
// Uses sessionStorage to persist across page reloads within same browser session
function DevAutoLogin() {
  const { user, loading } = useAuth();

  useEffect(() => {
    // Skip if not in dev mode, still loading, or already logged in
    if (!import.meta.env.DEV || loading || user) return;

    // Skip if auto-login was already attempted in this session
    if (sessionStorage.getItem(DEV_AUTO_LOGIN_KEY)) return;

    const email = import.meta.env.VITE_DEV_EMAIL;
    const password = import.meta.env.VITE_DEV_PASSWORD;

    if (email && password) {
      // Mark as attempted BEFORE trying to login
      sessionStorage.setItem(DEV_AUTO_LOGIN_KEY, "true");

      console.log("🔧 Dev mode: auto-login as", email);
      signInWithEmailAndPassword(auth, email, password)
        .then(() => {
          console.log("✅ Auto-login successful");
        })
        .catch((err) => {
          console.warn("⚠️ Auto-login failed:", err.message);
        });
    }
  }, [user, loading]);

  return null;
}

// Protected route wrapper - saves current location for post-login redirect
function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return <div>Loading...</div>;
  }

  if (!user) {
    // Save the intended destination (including search params) for post-login redirect
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  return <>{children}</>;
}

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <ConfigProvider
        theme={{
          cssVar: { prefix: 'ant' },
          token: {
            colorPrimary: "#1890ff",
            borderRadius: 4,
          },
        }}
      >
        <AntApp>
          <DevAutoLogin />
          <BrowserRouter>
            <Routes>
              <Route path="/login" element={<LoginPage />} />
              <Route path="/beta" element={<BetaPage />} />
              <Route path="/pricing" element={<Navigate to="/account" replace />} />
              <Route path="/projects-demo" element={<ProjectsDemoPage />} />
              <Route
                path="/"
                element={
                  <ProtectedRoute>
                    <ProjectsPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/photos/:viewId"
                element={
                  <ProtectedRoute>
                    <PhotosPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/test-suite/:viewId"
                element={
                  <ProtectedRoute>
                    <TestSuitePage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/report/:viewId"
                element={
                  <ProtectedRoute>
                    <ReportPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/mobile-app"
                element={
                  <ProtectedRoute>
                    <MobileAppPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/map"
                element={
                  <ProtectedRoute>
                    <MapPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/videos"
                element={
                  <ProtectedRoute>
                    <VideosPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/sync"
                element={
                  <ProtectedRoute>
                    <CloudSyncPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/account"
                element={
                  <ProtectedRoute>
                    <AccountPage />
                  </ProtectedRoute>
                }
              />
              <Route path="/plans" element={<Navigate to="/account" replace />} />
              <Route
                path="/usage"
                element={
                  <ProtectedRoute>
                    <UsagePage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin"
                element={
                  <ProtectedRoute>
                    <AdminGuard>
                      <AdminPage />
                    </AdminGuard>
                  </ProtectedRoute>
                }
              >
                <Route index element={<Navigate to="subscriptions" replace />} />
                <Route path="users-stats" element={<UsersStatsPage />} />
                <Route path="mobile-params" element={<MobileParamsPage />} />
                <Route path="plans" element={<PlansPage />} />
                <Route path="cloud-limits" element={<CloudLimitsPage />} />
                <Route path="usb-catalog" element={<UsbCatalogPage />} />
                <Route path="subscriptions" element={<SubscriptionsPage />} />
                <Route path="beta-codes" element={<BetaCodesPage />} />
                <Route path="service-access" element={<ServiceAccessPage />} />
              </Route>
              {/* Public shared project pages (no auth required) */}
              <Route path="/s/:shareToken" element={<SharedProjectPage />} />
              <Route path="/s/:shareToken/:viewId" element={<SharedGalleryPage />} />
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </BrowserRouter>
        </AntApp>
      </ConfigProvider>
    </QueryClientProvider>
  );
}

export default App;
